/**
 * InquiryPage - Submit inquiry for hosted class or remote notification
 * Works for both authenticated users and guests (no login required)
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getEventDetails, type TourStop } from '../../api/events';
import { getFriendlyErrorMessage } from '../../api/client-error';
import {
  submitInquiry,
  submitGuestInquiry,
  type HostedClassInquiry,
  type RemoteStudioInquiry,
  type GeneralInquiry,
  type BookingClosedInquiry,
} from '../../api/inquiries';
import SmsConsentCheckbox from '../../components/ui/SmsConsentCheckbox';
import { buildLocationParams } from '../../utils/location';

type InquiryType = 'hosted' | 'remote' | 'general';

export default function InquiryPage() {
  const { tourStopId } = useParams<{ tourStopId: string }>();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();

  const inquiryType = (searchParams.get('type') as InquiryType) || 'general';
  const showIdFromUrl = searchParams.get('showId') || '';
  const showNameFromUrl = searchParams.get('showName') || '';
  const zipFromUrl = searchParams.get('zip') || '';
  const latFromUrl = searchParams.get('lat') || '';
  const lngFromUrl = searchParams.get('lng') || '';
  const qFromUrl = searchParams.get('q') || '';
  const closedDate = searchParams.get('date');
  const inquiryReason = searchParams.get('reason');

  const [event, setEvent] = useState<TourStop | null>(null);
  const [loading, setLoading] = useState(!!tourStopId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ token: string } | null>(null);

  // Guest contact info
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  // Optional SMS/text-message opt-in (A2P/10DLC) for guest submissions — never blocks submit.
  const [smsConsent, setSmsConsent] = useState(false);
  const [guestStudioName, setGuestStudioName] = useState('');

  // Form state - Hosted Class
  // Venue details removed per SDA-158 — SDA team locates the venue on the
  // studio's behalf after the inquiry comes in.
  const [preferredDate1, setPreferredDate1] = useState('');
  const [preferredDate2, setPreferredDate2] = useState('');
  const [preferredDate3, setPreferredDate3] = useState('');
  const [studioWillTravel, setStudioWillTravel] = useState(false);

  // Form state - Remote Studio
  const [preferredCities, setPreferredCities] = useState('');
  const [notifyWhenCloser, setNotifyWhenCloser] = useState(true);

  // Form state - General
  const isBookingClosed = inquiryReason === 'booking_closed' && !!closedDate;
  const formattedClosedDate = closedDate
    ? new Date(closedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : '';
  const [message, setMessage] = useState(
    isBookingClosed
      ? `Interested in booking a class on ${formattedClosedDate}. Direct booking was closed for this date.`
      : ''
  );

  // Compute available Saturdays within tour date range
  const availableSaturdays = useMemo(() => {
    if (!event) return [];
    const start = new Date(event.start_date + 'T00:00:00');
    const end = new Date(event.end_date + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const effectiveStart = start > today ? start : today;

    const first = new Date(effectiveStart);
    const dayOfWeek = first.getDay();
    const daysUntilSat = dayOfWeek === 6 ? 0 : (6 - dayOfWeek);
    first.setDate(first.getDate() + daysUntilSat);

    const saturdays: string[] = [];
    for (let d = new Date(first); d <= end; d.setDate(d.getDate() + 7)) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      saturdays.push(`${year}-${month}-${day}`);
    }
    return saturdays;
  }, [event]);

  // Build back URL preserving location params so EventDetailPage doesn't re-prompt
  const backUrl = useMemo(() => {
    if (!event) return '/';
    const qs = buildLocationParams({ zip: zipFromUrl, lat: latFromUrl, lng: lngFromUrl, q: qFromUrl });
    return `/events/${event.id}${qs ? `?${qs}` : ''}`;
  }, [event, zipFromUrl, latFromUrl, lngFromUrl, qFromUrl]);

  // Load event details if tourStopId provided
  useEffect(() => {
    if (!tourStopId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    getEventDetails(tourStopId)
      .then(setEvent)
      .catch((err) => setError(getFriendlyErrorMessage(err, 'Failed to load event details')))
      .finally(() => setLoading(false));
  }, [tourStopId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const showId = event?.show.id || showIdFromUrl;
      const showName = event?.show.name || showNameFromUrl;

      if (!showId || !showName) {
        throw new Error('Show information is required');
      }

      // Validate guest contact info
      if (!isAuthenticated) {
        if (!guestName || !guestEmail || !guestStudioName) {
          throw new Error('Please fill in your name, email, and studio name');
        }
      }

      let inquiryData: HostedClassInquiry | RemoteStudioInquiry | GeneralInquiry | BookingClosedInquiry;

      if (inquiryType === 'hosted') {
        if (!preferredDate1) {
          throw new Error('Please provide at least one preferred date');
        }
        if (!studioWillTravel) {
          throw new Error('Please confirm that your studio will travel to the venue');
        }

        const preferredDates = [preferredDate1, preferredDate2, preferredDate3].filter(Boolean);

        inquiryData = {
          showId,
          showName,
          tourStopId: event?.id,
          source: 'dual_option_hosted' as const,
          message,
          preferredDates,
          studioWillTravel,
        };
      } else if (inquiryType === 'remote') {
        const cities = preferredCities
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean);

        inquiryData = {
          showId,
          showName,
          tourStopId: event?.id,
          source: 'remote_request' as const,
          message,
          preferredCities: cities,
          notifyWhenCloser,
        };
      } else if (isBookingClosed) {
        inquiryData = {
          showId,
          showName,
          tourStopId: event?.id,
          source: 'booking_closed' as const,
          message,
          preferredDates: [closedDate!],
        } satisfies BookingClosedInquiry;
      } else {
        inquiryData = {
          showId,
          showName,
          tourStopId: event?.id,
          source: 'general' as const,
          message,
        };
      }

      if (isAuthenticated) {
        const response = await submitInquiry(inquiryData);
        setSuccess({ token: response.token });
      } else {
        const response = await submitGuestInquiry({
          ...inquiryData,
          contactName: guestName,
          contactEmail: guestEmail,
          contactPhone: guestPhone || undefined,
          studioName: guestStudioName,
          sms_consent: smsConsent,
        });
        setSuccess({ token: response.token });
      }
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Failed to submit inquiry'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <div className="card text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-display font-bold text-warm-900 mb-2">Inquiry Submitted!</h1>
            <p className="text-warm-600 mb-6">
              Thank you for your interest. We'll review your inquiry and get back to you within 24 hours.
            </p>

            {isAuthenticated ? (
              <>
                <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/dashboard/inquiries" className="btn-primary">
                    View My Inquiries
                  </Link>
                  <Link to="/" className="btn-secondary">
                    Browse More Classes
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="mt-6 border-t border-warm-200 pt-6">
                  <h3 className="font-semibold text-warm-900 mb-2">
                    Want to track this inquiry and book classes directly?
                  </h3>
                  <p className="text-sm text-warm-600 mb-4">
                    Create a free account using <strong>{guestEmail}</strong> and this inquiry
                    will automatically appear in your dashboard.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to={`/register?email=${encodeURIComponent(guestEmail)}`} className="btn-primary">
                      Create Free Account
                    </Link>
                    <Link to="/" className="btn-secondary">
                      Maybe Later
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  const showName = event?.show.name || showNameFromUrl || 'Broadway Class';

  return (
    <div className="min-h-screen bg-warm-50">
      <Header />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={backUrl}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium mb-4 inline-flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </Link>

          <h1 className="text-2xl font-display font-bold text-warm-900">
            {inquiryType === 'hosted'
              ? 'Request Custom Masterclass'
              : inquiryType === 'remote'
              ? 'Notify Me When Closer'
              : 'Request Information'}
          </h1>
          <p className="text-warm-600 mt-2">
            {inquiryType === 'hosted'
              ? `Interested in a customized Broadway Touring Masterclass with a cast member from ${showName}? Fill out the details below and we'll reach out with options and pricing.`
              : inquiryType === 'remote'
              ? `We'll notify you when ${showName} is performing closer to your area.`
              : `Have questions about ${showName}? Let us know.`}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3">
            <svg className="w-5 h-5 text-danger-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
          </div>
        )}

        {isBookingClosed && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="font-medium text-amber-900">Inquiry for closed date: {formattedClosedDate}</p>
              <p className="text-sm text-amber-700 mt-1">
                Direct booking was closed for this date. We'll review your request and reach out if we can accommodate it.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Show Info Display */}
          {event && (
            <div className="card bg-warm-50">
              <div className="flex items-center gap-4">
                {event.show.logo_url && (
                  <img
                    src={event.show.logo_url}
                    alt={event.show.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                )}
                <div>
                  <h3 className="font-semibold text-warm-900">{event.show.name}</h3>
                  <p className="text-sm text-warm-600">
                    {event.theater.name} · {event.city.name}, {event.city.state}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Guest Contact Info */}
          {!isAuthenticated && (
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Your Information</h2>
              <p className="text-sm text-warm-500 mb-4">
                Tell us about yourself so we can get in touch.
              </p>

              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="guestName" className="block text-sm font-medium text-warm-700 mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="guestName"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Jane Smith"
                      className="input w-full"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="guestStudioName" className="block text-sm font-medium text-warm-700 mb-1">
                      Studio Name *
                    </label>
                    <input
                      type="text"
                      id="guestStudioName"
                      value={guestStudioName}
                      onChange={(e) => setGuestStudioName(e.target.value)}
                      placeholder="Main Street Dance Studio"
                      className="input w-full"
                      required
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="guestEmail" className="block text-sm font-medium text-warm-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="guestEmail"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="jane@dancestudio.com"
                      className="input w-full"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="guestPhone" className="block text-sm font-medium text-warm-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="guestPhone"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      className="input w-full"
                    />
                    <SmsConsentCheckbox checked={smsConsent} onChange={setSmsConsent} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hosted Class Form */}
          {inquiryType === 'hosted' && (
            <>
              {/* Venue Details section removed per SDA-158 — SDA team sources the venue
                  on the studio's behalf after the inquiry is submitted. */}

              <div className="card">
                <h2 className="text-lg font-semibold text-warm-900 mb-4">Preferred Dates</h2>
                <p className="text-sm text-warm-500 mb-4">
                  Select up to 3 preferred Saturdays for the masterclass.
                  {event && ` Available dates are between the tour dates (${new Date(event.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(event.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}).`}
                </p>

                {availableSaturdays.length === 0 ? (
                  <p className="text-sm text-warning-600">
                    No available Saturdays found within the tour date range.
                  </p>
                ) : (
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="preferredDate1" className="block text-sm font-medium text-warm-700 mb-1">
                        First Choice *
                      </label>
                      <select
                        id="preferredDate1"
                        value={preferredDate1}
                        onChange={(e) => setPreferredDate1(e.target.value)}
                        className="input w-full"
                        required
                      >
                        <option value="">Select a Saturday</option>
                        {availableSaturdays.map((d) => (
                          <option key={d} value={d} disabled={d === preferredDate2 || d === preferredDate3}>
                            {formatSaturdayLabel(d)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="preferredDate2" className="block text-sm font-medium text-warm-700 mb-1">
                        Second Choice
                      </label>
                      <select
                        id="preferredDate2"
                        value={preferredDate2}
                        onChange={(e) => setPreferredDate2(e.target.value)}
                        className="input w-full"
                      >
                        <option value="">Select a Saturday</option>
                        {availableSaturdays.map((d) => (
                          <option key={d} value={d} disabled={d === preferredDate1 || d === preferredDate3}>
                            {formatSaturdayLabel(d)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="preferredDate3" className="block text-sm font-medium text-warm-700 mb-1">
                        Third Choice
                      </label>
                      <select
                        id="preferredDate3"
                        value={preferredDate3}
                        onChange={(e) => setPreferredDate3(e.target.value)}
                        className="input w-full"
                      >
                        <option value="">Select a Saturday</option>
                        {availableSaturdays.map((d) => (
                          <option key={d} value={d} disabled={d === preferredDate1 || d === preferredDate2}>
                            {formatSaturdayLabel(d)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="card bg-primary-50 border-primary-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={studioWillTravel}
                    onChange={(e) => setStudioWillTravel(e.target.checked)}
                    className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                    required
                  />
                  <div>
                    <span className="font-medium text-warm-900">
                      I confirm that our studio/students will travel to the venue *
                    </span>
                    <p className="text-sm text-warm-600 mt-1">
                      For hosted classes, your studio is responsible for bringing students to the venue location.
                      The Broadway cast member will meet you there.
                    </p>
                  </div>
                </label>
              </div>
            </>
          )}

          {/* Remote Studio Form */}
          {inquiryType === 'remote' && (
            <div className="card">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">Notification Preferences</h2>
              <p className="text-sm text-warm-500 mb-4">
                We'll notify you when {showName} is performing closer to your area.
              </p>

              <div className="space-y-4">
                <div>
                  <label htmlFor="preferredCities" className="block text-sm font-medium text-warm-700 mb-1">
                    Preferred Cities
                  </label>
                  <input
                    type="text"
                    id="preferredCities"
                    value={preferredCities}
                    onChange={(e) => setPreferredCities(e.target.value)}
                    placeholder="e.g., Boston, Providence, Hartford (comma-separated)"
                    className="input w-full"
                  />
                  <p className="text-xs text-warm-500 mt-1">
                    Enter cities you'd be willing to travel to, separated by commas.
                  </p>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifyWhenCloser}
                    onChange={(e) => setNotifyWhenCloser(e.target.checked)}
                    className="mt-1 w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                  />
                  <div>
                    <span className="font-medium text-warm-900">
                      Email me when this class is performing within 75 miles
                    </span>
                    <p className="text-sm text-warm-600 mt-1">
                      We'll send you an email with booking options when the tour comes to your area.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Message (all types) */}
          <div className="card">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              {inquiryType === 'general' ? 'Your Message' : 'Additional Information'}
            </h2>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                inquiryType === 'hosted'
                  ? 'Any additional details...'
                  : inquiryType === 'remote'
                  ? 'Tell us more about your studio and what you\'re looking for...'
                  : 'How can we help you?'
              }
              rows={4}
              className="input w-full"
              maxLength={2000}
            />
            <p className="text-xs text-warm-500 mt-1">{message.length}/2000 characters</p>
          </div>

          {/* Contact Info Display (authenticated users) */}
          {isAuthenticated && (
            <div className="card bg-warm-50">
              <h3 className="font-medium text-warm-900 mb-2">Contact Information</h3>
              <p className="text-sm text-warm-600">
                We'll respond to: <strong>{user?.email}</strong>
              </p>
              <p className="text-xs text-warm-500 mt-1">
                This is the email associated with your account.
              </p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Submitting...
              </span>
            ) : (
              'Submit Inquiry'
            )}
          </button>

          <p className="text-center text-sm text-warm-500">
            We typically respond within 24 hours.
          </p>
        </form>
      </main>
    </div>
  );
}

/**
 * Format YYYY-MM-DD as a readable Saturday label
 */
function formatSaturdayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Header component - adapts to auth state
 */
function Header() {
  const { user, isAuthenticated, logout } = useAuth();

  return (
    <header className="bg-white border-b border-warm-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>

          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-warm-600 hover:text-warm-900 text-sm">
                  Dashboard
                </Link>
                <span className="text-warm-400 text-sm hidden sm:block">{user?.email}</span>
                <button
                  onClick={() => logout()}
                  className="text-warm-600 hover:text-warm-900 text-sm"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-warm-600 hover:text-warm-900 text-sm">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-sm px-4 py-2">
                  Create Account
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
